import { reaction } from "mobx";
import { LoginAnalyticsEvent } from "healthex-common/analytics.dtos";
import { FeatureFlag } from "healthex-common/features.dtos";
import { IdpCompatibilityMode } from "healthex-common/organization.dtos";
import { GFIDProvider } from "healthex-common/healthcareorganization.dtos";
import { asEnum } from "healthex-common/utils";
import { LoginSource } from "healthex-common/heap.dtos";
import { StudyVersionStatus } from "healthex-common/study.dtos";
import { getHeapGlobalEventProperties } from "../Infra/LoadHeap";
import { LoginFlow } from "../Logic/AuthManager";
import { NamedRoutes } from "./NamedRoutes";
import { effectiveQueryParams } from "./queryParams";
import { routeEquals } from "./RoutingUIManager";
import { HIGHER_AUTH_TIER_REQUIRED } from "./SocialLoginAuthManager";
import { generateOAuthState, getAnonymousUserId } from "../Logic/AuthManager";
import { buildHostedRedirectUrl, isHostedRedirectUriAllowed } from "./hostedRedirect";
import { deriveCodeChallenge, generateCodeVerifier } from "./pkce";
export class HostedIdpSsoManager {
    constructor(persistence, routingUIManager, studyManager, authManager, featureManager, socialLoginAuthManager, patientService, analyticsService, environment) {
        this.persistence = persistence;
        this.routingUIManager = routingUIManager;
        this.studyManager = studyManager;
        this.authManager = authManager;
        this.featureManager = featureManager;
        this.socialLoginAuthManager = socialLoginAuthManager;
        this.patientService = patientService;
        this.analyticsService = analyticsService;
        this.environment = environment;
        reaction(() => this.routingUIManager.currentRoute, (currentRoute) => {
            if (routeEquals(currentRoute, NamedRoutes.PATIENT_CONSENT_IDP_CALLBACK)) {
                void this.handleCallback();
            }
        }, { fireImmediately: true });
    }
    async maybeStartSso(studyId, iss) {
        if (!this.featureManager.isFeatureEnabled(FeatureFlag.HOSTED_IDP_SSO_ENABLED)) {
            console.log("[HostedFlow] maybeStartSso aborted: HOSTED_IDP_SSO_ENABLED is not enabled for this org.");
            return;
        }
        if (this.authManager.isLoggedIn && !this.authManager.isHostedSsoRestricted) {
            console.log(`[HostedFlow] Skipping hosted-SSO redirect: device already has a full-scope session; proceeding with existing token.`);
            return;
        }
        const allowed = await this.studyManager.isIdpIssuerAllowedForStudy(studyId, iss);
        if (!allowed) {
            console.log(`[HostedFlow] iss validation failed: "${iss}" is not in the allowed IdP issuer list for study ${studyId}; clearing any existing iss from sessionStorage.`);
            this.persistence.setIss(null);
            return;
        }
        console.log(`[HostedFlow] iss validation succeeded: "${iss}" is allowed for study ${studyId}; persisting to sessionStorage.`);
        this.persistence.setIss(iss);
        this.persistence.setHostedSsoStudyId(studyId);
        await this.redirectToIdp(studyId, iss);
    }
    async redirectToIdp(studyId, iss) {
        var _a, _b, _c;
        const request = await this.studyManager.getHostedSsoAuthorizationRequest(studyId, iss);
        const isLegacy = request.mode === IdpCompatibilityMode.LEGACY;
        const redirectUri = isLegacy ? `${window.location.origin}/#/patient-consent/idp-callback` : request.redirectUri;
        const state = generateOAuthState();
        this.persistence.setHostedSsoState(state);
        await this.analyticsService.trackHeapEvent({
            event: LoginAnalyticsEvent.LOGIN_ATTEMPTED,
            user_id: (_a = getAnonymousUserId()) !== null && _a !== void 0 ? _a : undefined,
            properties: { ...this.buildHostedHeapProperties() },
        });
        const authorizeUrl = new URL(request.authorizationEndpoint);
        authorizeUrl.searchParams.set("state", state);
        authorizeUrl.searchParams.set("redirect_uri", redirectUri !== null && redirectUri !== void 0 ? redirectUri : "");
        if (isLegacy) {
            this.persistence.setHostedSsoNonce(null);
            this.persistence.setHostedSsoCodeVerifier(null);
        }
        else {
            const nonce = generateOAuthState();
            const codeVerifier = generateCodeVerifier();
            this.persistence.setHostedSsoNonce(nonce);
            this.persistence.setHostedSsoCodeVerifier(codeVerifier);
            authorizeUrl.searchParams.set("response_type", "code");
            authorizeUrl.searchParams.set("client_id", (_b = request.clientId) !== null && _b !== void 0 ? _b : "");
            authorizeUrl.searchParams.set("scope", (_c = request.scope) !== null && _c !== void 0 ? _c : "openid profile email");
            authorizeUrl.searchParams.set("nonce", nonce);
            authorizeUrl.searchParams.set("code_challenge", await deriveCodeChallenge(codeVerifier));
            authorizeUrl.searchParams.set("code_challenge_method", "S256");
            if (request.maxAge !== undefined)
                authorizeUrl.searchParams.set("max_age", String(request.maxAge));
        }
        console.log(`[HostedFlow] Redirecting to IdP (${request.mode}): ${authorizeUrl.toString()} with state: ${state}`);
        window.location.href = authorizeUrl.toString();
    }
    async handleCallback() {
        var _a, _b, _c, _d, _e;
        const queryParams = effectiveQueryParams();
        const code = queryParams.get("code");
        const incomingState = queryParams.get("state");
        const storedState = this.persistence.getHostedSsoState();
        const iss = this.persistence.getIss();
        const studyId = this.persistence.getHostedSsoStudyId();
        console.log(`Received IDP callback with state: ${incomingState} for the stored state: ${storedState}`);
        try {
            if (!code || !incomingState) {
                throw new Error("IdP callback missing code or state");
            }
            if (!storedState || incomingState !== storedState) {
                throw new Error(`IdP callback state mismatch (incoming=${incomingState}, stored=${storedState})`);
            }
            if (!iss || !studyId) {
                throw new Error("IdP callback missing iss or studyId from sessionStorage");
            }
            const result = await this.studyManager.exchangeHostedSsoCode(studyId, code, {
                iss,
                nonce: (_a = this.persistence.getHostedSsoNonce()) !== null && _a !== void 0 ? _a : undefined,
                codeVerifier: (_b = this.persistence.getHostedSsoCodeVerifier()) !== null && _b !== void 0 ? _b : undefined,
            }, this.buildHostedHeapProperties());
            if (result.error === HIGHER_AUTH_TIER_REQUIRED) {
                if (!result.linkingToken) {
                    throw new Error("HIGHER_AUTH_TIER_REQUIRED but no linkingToken returned");
                }
                console.log(`[HostedFlow] Account requires stronger authentication (${(_c = result.requiredProviders) === null || _c === void 0 ? void 0 : _c.join(", ")}); routing to account linking.`);
                this.socialLoginAuthManager.enterAccountLinkingRequiredState(result.linkingToken, GFIDProvider.HOSTED_SSO, (_d = result.requiredProviders) === null || _d === void 0 ? void 0 : _d.flatMap((p) => {
                    const provider = asEnum(GFIDProvider)(p);
                    return provider ? [provider] : [];
                }));
            }
            else {
                if (!result.token) {
                    throw new Error(`Exchange returned no session token and no recognised linking signal (error: ${result.error})`);
                }
                console.log(`[HostedFlow] Backend code exchange succeeded for patient: ${result.patientId} with state: ${incomingState}`);
                await this.authManager.handleSocialLogin(result.token, LoginFlow.GFID_HOSTED_SSO, result.isFirstTimeLogin);
            }
            this.persistence.setHostedSsoState(null);
            this.persistence.setHostedSsoStudyId(null);
            this.persistence.setHostedSsoNonce(null);
            this.persistence.setHostedSsoCodeVerifier(null);
            const cleanedUrl = new URL(window.location.href);
            cleanedUrl.searchParams.delete("code");
            cleanedUrl.searchParams.delete("state");
            window.history.replaceState(null, "", cleanedUrl.toString());
            console.log(`[HostedFlow] Redirecting back to enrollment/link for study ${studyId} with state: ${incomingState}.`);
            this.routingUIManager.redirectTo(NamedRoutes.PATIENT_CONSENT_ENROLLMENT, { studyId, step: "link" });
        }
        catch (e) {
            console.log("[HostedFlow] Callback failed; redirecting partner with status=failed:", e);
            await this.analyticsService.trackHeapEvent({
                event: LoginAnalyticsEvent.LOGIN_ERROR,
                user_id: (_e = getAnonymousUserId()) !== null && _e !== void 0 ? _e : undefined,
                properties: { ...this.buildHostedHeapProperties(), error: e instanceof Error ? e.message : String(e) },
            });
            await this.redirectToPartnerWithFailure(studyId);
        }
    }
    async redirectToPartnerWithFailure(studyId) {
        var _a;
        const redirectUri = this.persistence.getRedirectUriForHostedFlow();
        const xid = this.unsignedExternalId();
        if (!redirectUri || !studyId) {
            console.warn("[HostedFlow] Cannot redirect partner on failure: missing redirect URI or studyId.");
            this.persistence.clearHostedFlowSessionState();
        }
        else {
            let allowedUrls = null;
            try {
                const study = await this.studyManager.getStudyPublicInfo(studyId, StudyVersionStatus.ACTIVE);
                allowedUrls = (_a = study === null || study === void 0 ? void 0 : study.hostedRedirectUrls) !== null && _a !== void 0 ? _a : [];
            }
            catch (e) {
                console.warn("[HostedFlow] Could not load study to validate the failure redirect; leaving hosted-flow state for retry:", e);
            }
            if (allowedUrls !== null) {
                this.persistence.clearHostedFlowSessionState();
                if (isHostedRedirectUriAllowed(redirectUri, allowedUrls)) {
                    window.location.href = buildHostedRedirectUrl(redirectUri, { status: "failed", xid });
                }
                else {
                    console.warn("[HostedFlow] Failure redirect URI not in the study's allowed list; not redirecting:", redirectUri);
                }
            }
        }
    }
    unsignedExternalId() {
        const signed = this.persistence.getSignedExternalId();
        if (!signed)
            return null;
        const lastDot = signed.lastIndexOf(".");
        return lastDot > 0 ? signed.slice(0, lastDot) : null;
    }
    buildHostedHeapProperties() {
        const studyId = this.persistence.getHostedSsoStudyId();
        return {
            ...getHeapGlobalEventProperties(),
            ...(studyId ? { projectId: studyId } : {}),
            provider: GFIDProvider.HOSTED_SSO,
            source: LoginSource.CONSENT_LOGIN,
        };
    }
}
